import { interaction, SizableText, Stack, XStack, YStack } from '@mythical/ui'
import { ArrowRight, Check } from '@tamagui/lucide-icons'
import config from 'app/config'
import { PlanLevel } from 'app/constants/tiers'
import { Perk } from 'app/queries/perks/perks'
import { Frequency, Tier } from 'app/queries/tiers/member-tiers'
import { useStores } from 'app/store'
import { useState } from 'react'
import { SolitoImage } from 'solito/image'

import { Button, ButtonLink } from '../../Button'
import { MemberfulLink } from '../../MemberfulLink'
import { Tag } from '../../Tag/Tag'
import { FrequencyToggle } from './FrequencyToggle'
import { TIER_THEME_MAP } from './themes'
import { IconArrowLeft } from 'app/components/primitive/Icon/icons/IconArrowLeft'

const MYTHICAL_BEAST_BADGE = require('app/assets/svg/become-a-mythical-beast.svg')

const TIER_BADGES: Partial<Record<PlanLevel, React.ReactNode>> = {
  2: (
    <SolitoImage
      alt="Become a mythical beast"
      height={198}
      src={MYTHICAL_BEAST_BADGE}
      width={193}
    />
  ),
}

const FREQUENCY_ORDER: Frequency[] = ['monthly', 'quarterly', 'annually']

const getFrequencies = (tier: Tier): Frequency[] =>
  [...new Set(tier.memberfulPlans?.map((p) => p.type))].sort(
    (a, b) => FREQUENCY_ORDER.indexOf(a) - FREQUENCY_ORDER.indexOf(b),
  )

const formatFrequency = (frequency: Frequency): string => {
  const frequencyMap: Record<Frequency, string> = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annually: 'Yearly',
  }
  return frequencyMap[frequency]
}

interface TierCardProps {
  tier: Tier & { description: string }
  perks: Perk[]
  displayGiftLink?: boolean
}

export const TierCard = ({
  tier,
  perks,
  displayGiftLink = false,
}: TierCardProps) => {
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false
  const [selectedFrequency, setSelectedFrequency] =
    useState<Frequency>('monthly')

  const theme = TIER_THEME_MAP[tier.planLevel]
  const plan = tier.memberfulPlans?.find((p) => p.type === selectedFrequency)
  const frequencies = getFrequencies(tier)
  const tierPerks = perks
    .filter((p) => p.tiers.includes(tier.planLevel))
    .map((p) => p.title)
  const badge = TIER_BADGES[tier.planLevel]

  return (
    <YStack
      bg={theme.bg}
      br="$5"
      f={1}
      gap="$8"
      pos="relative"
      px="$3.5"
      py="$7"
      w="100%"
      $gtMd={{
        px: '$9',
        py: '$11',
      }}
    >
      <YStack gap="$6" flex={1}>
        <YStack gap="$3.5" h={70} ai="center">
          <FrequencyToggle
            onChange={setSelectedFrequency}
            options={frequencies}
            selected={selectedFrequency}
            theme={theme}
          />

          {plan?.savings ? (
            <Tag
              displaySize="md"
              variant={theme.tagTheme}
              textProps={{ fow: '500' }}
            >
              Save {plan.savings}% with {formatFrequency(plan.type)}
            </Tag>
          ) : null}
        </YStack>

        <YStack gap="$3.5">
          <SizableText
            color={theme.textColor}
            textTransform="uppercase"
            size="$9.5"
            fow="800"
            $gtSm={{ size: '$10.5', fow: '800' }}
            $gtLg={{ size: '$12', fow: '800' }}
          >
            {tier.name}
          </SizableText>

          <SizableText
            ff="$title"
            color={theme.textColor}
            size="$m"
            $gtSm={{ size: '$l' }}
          >
            ${plan?.price || '0'} /mo
          </SizableText>

          <SizableText
            ff="$title"
            color={theme.textColor}
            size="$m"
            $gtSm={{ size: '$l' }}
          >
            {tier.description}
          </SizableText>
        </YStack>

        <YStack gap="$3.5">
          {tierPerks.map((perk, i) => (
            <XStack key={i} ai="flex-start" gap="$3.5">
              <Stack fs={0} w={24} h={24}>
                <Check col={theme.textColor as string} />
              </Stack>

              <SizableText
                fs={1}
                ff="$title"
                color={theme.textColor}
                size="$m"
                $gtSm={{ size: '$l' }}
              >
                {perk}
              </SizableText>
            </XStack>
          ))}
        </YStack>
      </YStack>

      {canMemberfulLink &&
        (() => {
          const href = plan?.externalId
            ? `${config.memberfulUrl}/checkout?plan=${plan.externalId}`
            : '/perks'

          const giftHref = plan?.externalId
            ? `${config.memberfulUrl}/gift?plan=${plan.externalId}`
            : `${config.memberfulUrl}/gift`

          const isMemberfulLink = href.includes('memberful.com')

          const buttonProps = {
            size: '$4' as const,
            scaleSpace: 0,
            textProps: {
              fontWeight: '500' as const,
            },
            theme: theme.buttonTheme,
            $gtMd: {
              w: 'fit-content' as const,
              px: '$7' as const,
            },
          }

          return (
            <YStack gap="$2" ai="flex-start">
              {isMemberfulLink ? (
                <MemberfulLink Component={Button} href={href} {...buttonProps}>
                  Join for ${plan?.price || '0'}&nbsp;/mo
                </MemberfulLink>
              ) : (
                <ButtonLink href={href} {...buttonProps}>
                  Join for ${plan?.price || '0'}&nbsp;/mo
                </ButtonLink>
              )}
              {displayGiftLink && canMemberfulLink && (
                <MemberfulLink
                  Component={Button}
                  href={giftHref}
                  size="$4"
                  theme="transparent"
                  px="$1"
                  iconAfter={
                    <IconArrowLeft
                      rotate="180deg"
                      size={20}
                      color={theme.textColor}
                    />
                  }
                  textProps={{ fontWeight: '500' }}
                  color={theme.textColor}
                  {...interaction.opacity}
                >
                  Gift this subscription
                </MemberfulLink>
              )}
            </YStack>
          )
        })()}

      {badge && (
        <Stack
          b={-54}
          display="none"
          pos="absolute"
          r={-24}
          zIndex={1}
          $gtMd={{ display: 'flex' }}
        >
          {badge}
        </Stack>
      )}
    </YStack>
  )
}
